import {
  EWarrantDecisionStatus, EWarrantDocument, EWarrantDocumentStatusPayload, EWarrantStatus
} from 'data/eWarrant/eWarrant.types';

/**
 * What the operator has decided about each document of an e-warrant, keyed by docId. A
 * docId absent from the map is undecided.
 *
 * One value per document, not a flag per checkbox: that is what makes the Approved and
 * Rejected boxes of a row mutually exclusive by construction, rather than by a rule
 * something has to remember to enforce.
 */
export type DocumentDecisions = { [docId: string]: EWarrantDecisionStatus };

/** Whether a status is one the operator decided, as opposed to how the document arrived. */
const isDecided = (status: EWarrantStatus): status is EWarrantDecisionStatus =>
  status === EWarrantStatus.Approved || status === EWarrantStatus.Rejected;

/**
 * A document the payload can address. `docId` is the document's own HI1OBJECTID, which the
 * backend reads out of Xcipio with `asText("")` — so a row can reach the UI without one,
 * and such a document cannot be decided at all.
 */
const isAddressable = (document: EWarrantDocument): boolean => document.docId !== '';

/**
 * The decisions Xcipio already holds, as the checkboxes should start out.
 *
 * Only a real decision seeds a box. A document that is still AwaitingApproval reads as
 * Pending, and treating anything that is not Rejected as Approved would silently
 * pre-approve every document of every e-warrant the moment the view loaded.
 */
export const seedDocumentDecisions = (documents: EWarrantDocument[]): DocumentDecisions =>
  documents.reduce((decisions: DocumentDecisions, document) => (
    isAddressable(document) && isDecided(document.docStatus)
      ? { ...decisions, [document.docId]: document.docStatus }
      : decisions
  ), {});

/**
 * The decisions after the operator clicked one of a document's two boxes.
 *
 * Ticking one box clears the other, and ticking a box that is already ticked clears it
 * again — a document may be Approved, Rejected, or back to undecided.
 */
export const toggleDocumentDecision = (
  decisions: DocumentDecisions,
  docId: string,
  status: EWarrantDecisionStatus,
): DocumentDecisions => {
  const next = { ...decisions };

  if (next[docId] === status) {
    delete next[docId];
  } else {
    next[docId] = status;
  }

  return next;
};

/**
 * Whether every document of the e-warrant is ticked Approved.
 *
 * Approving an e-warrant is approving what it carries, so anything less — an undecided
 * document, or one the operator ticked Rejected — leaves the Approve button closed.
 * Rejecting is not gated this way; a rejection stands on its own.
 *
 * Vacuously true for an e-warrant with no documents, which is why one stays approvable.
 * Documents that arrived without a docId are excluded rather than counted as undecided:
 * they cannot be addressed in the payload, so gating on them would make the button
 * impossible to press instead of merely disabled.
 */
export const areAllDocumentsApproved = (
  documents: EWarrantDocument[],
  decisions: DocumentDecisions,
): boolean =>
  documents
    .filter(isAddressable)
    .every((document) => decisions[document.docId] === EWarrantStatus.Approved);

/**
 * The documents to send with the decision — only the ones the operator actually decided.
 * An empty list tells the backend to write no document at all.
 *
 * Driven off `documents` rather than off the keys of `decisions`, so a decision left over
 * for a document that has since disappeared from the e-warrant cannot be sent.
 */
export const buildDocumentStatusPayload = (
  documents: EWarrantDocument[],
  decisions: DocumentDecisions,
): EWarrantDocumentStatusPayload[] =>
  documents
    .filter((document) => isAddressable(document) && decisions[document.docId] != null)
    .map((document) => ({ DocId: document.docId, DocStatus: decisions[document.docId] }));
